<template>
  <div class="w-full relative">
    <div class="flex justify-between text-xs">
      <div v-if="!previewSrc" 
           class="border-2 border-gray-300 border-dashed rounded-lg p-6 w-full" 
           :class="[sizeClasses, { 'border-primary': isDragging }]"
           @dragover.prevent="isDragging = true" 
           @dragleave.prevent="isDragging = false" 
           @drop.prevent="handleDrop">
        <input type="file" class="absolute inset-0 w-full h-full opacity-0 z-50" @change="handleFileInput" ref="fileInput" />
        <div class="text-center">
          <img class="mx-auto h-12 w-12" src="https://www.svgrepo.com/show/357902/image-upload.svg" alt="Upload Icon" />
          <h3 class="mt-2 text-sm font-medium text-gray-900">
            <label class="relative cursor-pointer">
              <span>Drag and drop</span>
              <span class="text-primary"> or browse</span>
              <span> to upload</span>
            </label>
          </h3>
          <p class="mt-1 text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
        </div>
      </div>
      <div v-else class="w-full" :class="previewSizeClasses">
        <div class="p-2 w-full h-full flex flex-col">
          <img v-if="previewSrc" :src="previewSrc" class="mx-auto" :class="imageSizeClasses" />
          <div class="mt-2 text-center">
            <a href="#" class="text-primary" @click.prevent="previewSrc=null">Reset</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';

const props = defineProps({
  modelValue: String, // Holds the preview URL
  size: {
    type: String,
    default: 'small',
    validator: (value) => ['small', 'medium', 'full'].includes(value)
  }
});

const emit = defineEmits(['update:modelValue', 'file-selected']); // Emits preview URL and file info

const isDragging = ref(false);
const previewSrc = ref(props.modelValue || null);

// Size classes
const sizeClasses = computed(() => {
  return {
    'small': 'max-h-40',
    'medium': 'max-h-80',
    'full': 'h-full'
  }[props.size];
});

const previewSizeClasses = computed(() => {
  return {
    'small': 'max-h-40',
    'medium': 'max-h-80',
    'full': 'h-full'
  }[props.size];
});

const imageSizeClasses = computed(() => {
  return {
    'small': 'max-h-32',
    'medium': 'max-h-64',
    'full': 'max-h-full max-w-full'
  }[props.size];
});

// Watch for external changes to the modelValue
watch(() => props.modelValue, (newVal) => {
  previewSrc.value = newVal;
});

const handleDrop = (event) => {
  isDragging.value = false;
  const file = event.dataTransfer?.files[0];
  if (file) handleFile(file);
};

const handleFileInput = (event) => {
  const file = event.target.files?.[0];
  if (file) handleFile(file);
};

const handleFile = (file) => {
  if (!file) return;

  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => {
    previewSrc.value = reader.result;
    emit('update:modelValue', previewSrc.value); // Send preview URL
    emit('file-selected', file); // Send full file data to the parent
  };
};
</script>
